<script>
	// Components
	import Helmet from '@/Components/Helmet.svelte';
	import { BetCategory } from '@/Constants/Bet';
	import ClassificationStore from '@/Stores/Racing/ClassificationStore';
	import TextInput from '@/Components/TextInput.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import { notify } from '@/Services/Notify';
	import { CurrencyFormat, NumberTransform } from '@/Services/Helpers';
	// Partials
	// Stores

	export let type = undefined;

	let title = '';
	let editingLiabilityLimits = [];
	let sending = false;

	$: betCategory = type && getBetCategory(type);
	$: betCategory && ClassificationStore.getClassifications(betCategory);

	function getBetCategory(givenType) {
		if (givenType.toLowerCase() === 'racing') {
			title = 'Racing';
			return BetCategory.Racing;
		} else if (givenType.toLowerCase() === 'sport') {
			title = 'Sport';
			return BetCategory.Sport;
		}
		return undefined;
	}

	function updateLiabilityLimit(classification) {
		sending = true;
		classification
			.updateLimit()
			.then(() => {
				notify({
					type: 'success',
					title: 'Liability limit updated',
					message: 'Liability limit updated successfully',
					timeout: 3
				});
				let indexToSplice = undefined;
				for (const index in editingLiabilityLimits) {
					if (editingLiabilityLimits[index] == classification.id) {
						indexToSplice = index;
					}
				}
				editingLiabilityLimits.splice(indexToSplice, 1);
				editingLiabilityLimits = editingLiabilityLimits;
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to update Liability limit ',
					timeout: 5
				});
			})
			.finally(() => {
				sending = false;
			});
	}
</script>

<Helmet title="Liability Limits" />
<div>
	<div>
		<!-- Page Header -->
		<div class="flex flex-col items-center w-full p-4 mb-4 text-sm bg-white border-b md:py-2">
			<div class="flex items-center w-full">
				<h1 class="text-xl font-bold">Liability Limits</h1>
			</div>
		</div>
		<!-- /Page Header -->

		<!-- Main Section -->
		<div class="relative flex justify-around">
			<div class={betCategory == BetCategory.Sport ? 'p-2 mx-2 w-2/3' : 'p-2 mx-2 w-1/2'}>
				<div class="rounded-b bg-white">
					<!-- Header -->
					<div class="flex items-center px-2 bg-dark-grey text-light rounded-t">
						<h2 class="flex-1 py-2 text-xl italic font-sans-condensed">{title} Liability limits</h2>
					</div>
					<!-- /Header -->
					<div class="m-3 p-3">
						<table class="w-full text-center">
							<tr>
								<th>Classification</th>
								<th class="text-left">Selection liability limit</th>
								<th class="text-left">Multi Selection liability limit</th>
								{#if betCategory == BetCategory.Sport}
									<th class="text-left">SGM Selection liability limit</th>
									<th class="text-left">SGM Multi Selection liability limit</th>
								{/if}
								<th />
							</tr>
							{#each $ClassificationStore.all() as classification (classification.id)}
								<tr class="h-10">
									<td>{classification.name}</td>
									<td class="text-left">
										<div class="flex flex-row justify-between items-center">
											{#if editingLiabilityLimits.includes(classification.id)}
												<TextInput
													name={`classification.${classification.id}.selection_liability_limit`}
													type="number"
													required={true}
													className="w-full text-left"
													placeholder="Enter selection liability limit"
													step="0.01"
													on:change={(event) =>
														(classification.selection_liability_limit =
															Math.round(parseFloat(event.target.value)) * 100)}
													on:keyup={(event) => {
														if (event.keyCode === 13) {
															classification.selection_liability_limit =
																Math.round(parseFloat(event.target.value)) * 100;
														}
													}}
													value={NumberTransform(classification.selection_liability_limit, false)}
												/>
											{:else}
												<span>
													{classification.selection_liability_limit === null ||
													isNaN(classification.selection_liability_limit)
														? 'None set'
														: CurrencyFormat(classification.selection_liability_limit)}
												</span>
											{/if}
										</div>
									</td>
									<td class="text-left">
										<div class="flex flex-row justify-between items-center">
											{#if editingLiabilityLimits.includes(classification.id)}
												<TextInput
													name={`classification.${classification.id}.multi_selections_liability_limit `}
													type="number"
													required={true}
													className="w-full text-left"
													placeholder="Enter multi selection liability limit"
													step="0.01"
													on:change={(event) =>
														(classification.multi_selections_liability_limit =
															Math.round(parseFloat(event.target.value)) * 100)}
													on:keyup={(event) => {
														if (event.keyCode === 13) {
															classification.multi_selections_liability_limit =
																Math.round(parseFloat(event.target.value)) * 100;
														}
													}}
													value={NumberTransform(classification.multi_selections_liability_limit, false)}
												/>
											{:else}
												<span>
													{classification.multi_selections_liability_limit === null ||
													isNaN(classification.multi_selections_liability_limit)
														? 'None set'
														: CurrencyFormat(classification.multi_selections_liability_limit)}
												</span>
											{/if}
										</div>
									</td>
									{#if betCategory == BetCategory.Sport}
										<td class="text-left">
											<div class="flex flex-row justify-between items-center">
												{#if editingLiabilityLimits.includes(classification.id)}
													<TextInput
														name={`classification.${classification.id}.sgm_selection_liability_limit`}
														type="number"
														required={true}
														className="w-full text-left"
														placeholder="Enter SGM selection liability limit"
														step="0.01"
														on:change={(event) =>
															(classification.sgm_selection_liability_limit =
																Math.round(parseFloat(event.target.value)) * 100)}
														on:keyup={(event) => {
															if (event.keyCode === 13) {
																classification.sgm_selection_liability_limit =
																	Math.round(parseFloat(event.target.value)) * 100;
															}
														}}
														value={NumberTransform(classification.sgm_selection_liability_limit, false)}
													/>
												{:else}
													<span>
														{classification.sgm_selection_liability_limit === null ||
														isNaN(classification.sgm_selection_liability_limit)
															? 'None set'
															: CurrencyFormat(classification.sgm_selection_liability_limit)}
													</span>
												{/if}
											</div>
										</td>
										<td class="text-left">
											<div class="flex flex-row justify-between items-center">
												{#if editingLiabilityLimits.includes(classification.id)}
													<TextInput
														name={`classification.${classification.id}.sgm_multi_selections_liability_limit `}
														type="number"
														required={true}
														className="w-full text-left"
														placeholder="Enter SGM multi selection liability limit"
														step="0.01"
														on:change={(event) =>
															(classification.sgm_multi_selections_liability_limit =
																Math.round(parseFloat(event.target.value)) * 100)}
														on:keyup={(event) => {
															if (event.keyCode === 13) {
																classification.sgm_multi_selections_liability_limit =
																	Math.round(parseFloat(event.target.value)) * 100;
															}
														}}
														value={NumberTransform(classification.sgm_multi_selections_liability_limit, false)}
													/>
												{:else}
													<span>
														{classification.sgm_multi_selections_liability_limit === null ||
														isNaN(classification.sgm_multi_selections_liability_limit)
															? 'None set'
															: CurrencyFormat(classification.sgm_multi_selections_liability_limit)}
													</span>
												{/if}
											</div>
										</td>
									{/if}
									<td class="text-right w-px">
										<div class="flex flex-row justify-center items-center">
											{#if editingLiabilityLimits.includes(classification.id)}
												<LoadingButton
													on:click={() => {
														updateLiabilityLimit(classification);
													}}
													className="flex flex-shrink items-center justify-center btn btn-primary"
													loading={sending}
													>Save
												</LoadingButton>
											{:else}
												<button
													on:click={() => {
														editingLiabilityLimits = [...editingLiabilityLimits, classification.id];
													}}
													class="flex flex-shrink items-center justify-center btn btn-primary-action"
												>
													<span>Change</span>
												</button>
											{/if}
										</div>
									</td>
								</tr>
							{/each}
						</table>
					</div>
				</div>
			</div>
		</div>
	</div>
</div>
